/**
 * What the payment pages know about the person, read off the device.
 *
 * `/payment/success` and `/payment/failed` are standalone routes — no
 * CheckProvider, and the documented API exposes no way to look a purchase up.
 * So everything they show comes from the check's own persisted state, with the
 * lead form's prefill as a fallback for the name.
 *
 * Every field is optional by design: someone may land here with cleared storage,
 * on a different device, or from a link. The pages must still read sensibly.
 */

import { PLANS, RESERVE_AMOUNT, REWARD_TOTAL, BONUS_BY_TRACK, type PlanKey } from "./content";
import { readLeadPrefill } from "./lead-prefill";
import type { Spoc } from "./miles360";

const KEY = "caira_check_v1";

export type PaymentContext = {
  name: string;
  /** Their own number, so a counsellor can be looked up when we have none stored. */
  phone: string;
  /** The counsellor assigned to them, when the purchase response gave us one. */
  spoc: Spoc | null;
  /** The track they chose, only when it still exists in PLANS. */
  track: PlanKey | null;
  planName: string;
  code: string;
  purchaseUuid: string | null;
  /** Their own enrolment form, so a failed payment can be retried without
   *  creating a second purchase. */
  enrollmentUrl: string | null;
  deadline: number | null;
  /** The money, matching the pricing screen exactly. */
  price: {
    fee: number;
    reward: number;
    bonus: number;
    net: number;
    paidNow: number;
    dueAtEnrolment: number;
    financed: number;
    emi: number;
    lastEmi: number;
  } | null;
};

const EMPTY: PaymentContext = {
  name: "",
  phone: "",
  spoc: null,
  track: null,
  planName: "",
  code: "",
  purchaseUuid: null,
  enrollmentUrl: null,
  deadline: null,
  price: null,
};

function str(v: unknown) {
  return typeof v === "string" ? v.trim() : "";
}

/** Only a counsellor with a name and a reachable number is worth showing. */
function readSpoc(v: unknown): Spoc | null {
  if (!v || typeof v !== "object" || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const name = str(o["name"]);
  const phone = str(o["phone"]).replace(/\D/g, "").slice(-10);
  if (!name || phone.length !== 10) return null;
  const email = str(o["email"]);
  const employeeId = str(o["employeeId"]);
  return {
    name,
    phone,
    ...(email ? { email } : {}),
    ...(employeeId ? { employeeId } : {}),
  };
}

function isTrack(v: unknown): v is PlanKey {
  return typeof v === "string" && Object.prototype.hasOwnProperty.call(PLANS, v);
}

/** The same arithmetic the pricing screen runs, kept in one place. */
export function priceFor(track: PlanKey): NonNullable<PaymentContext["price"]> {
  const fee = PLANS[track].fee;
  const bonus = BONUS_BY_TRACK[track] ?? 0;
  const reward = REWARD_TOTAL + bonus;
  const net = fee - reward;
  const dueAtEnrolment = RESERVE_AMOUNT;
  const financed = net - RESERVE_AMOUNT - dueAtEnrolment;
  const emi = Math.ceil(financed / 12);
  return {
    fee,
    reward,
    bonus,
    net,
    paidNow: RESERVE_AMOUNT,
    dueAtEnrolment,
    financed,
    emi,
    lastEmi: financed - emi * 11,
  };
}

export function readPaymentContext(storage?: Pick<Storage, "getItem">): PaymentContext {
  const store = storage ?? (typeof localStorage === "undefined" ? null : localStorage);
  if (!store) return EMPTY;

  let saved: Record<string, unknown> = {};
  try {
    const raw = store.getItem(KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      saved = parsed as Record<string, unknown>;
    }
  } catch {
    /* cleared, blocked or corrupt storage — the pages cope with knowing nothing */
  }

  const phone = str(saved["phone"]).replace(/\D/g, "").slice(-10);
  let name = str(saved["name"]);
  if (!name) {
    try {
      name = readLeadPrefill().name;
    } catch {
      /* ignore */
    }
  }

  const track = isTrack(saved["track"]) ? saved["track"] : null;
  const deadline = typeof saved["deadline"] === "number" ? saved["deadline"] : null;

  return {
    name,
    phone,
    spoc: readSpoc(saved["spoc"]),
    track,
    planName: track ? PLANS[track].name : "",
    code: str(saved["code"]),
    purchaseUuid: str(saved["purchaseUuid"]) || null,
    enrollmentUrl: str(saved["enrollmentUrl"]) || null,
    deadline,
    price: track ? priceFor(track) : null,
  };
}
